import { Component, OnDestroy, OnInit, inject, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, combineLatest, map, timer } from 'rxjs';
import { ReadyBoardDataService } from '../../core/ready-board/ready-board-data.service';
import { ReadyBoardConnectionService } from '../../core/ready-board/ready-board-connection.service';
import { ServerTimeService } from '../../core/server-time.service';

@Component({
    selector: 'app-internal-display',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './internal-display.component.html',
    styleUrl: './internal-display.component.scss'
})
export class InternalDisplayComponent implements OnInit, OnDestroy {
    private readonly readyBoardData = inject(ReadyBoardDataService);
    private readonly connectionService = inject(ReadyBoardConnectionService);
    private readonly serverTime = inject(ServerTimeService);
    private readonly ngZone = inject(NgZone);
    private reloadHandle: ReturnType<typeof setTimeout> | undefined;

    private readonly timeFormatter = new Intl.DateTimeFormat('ro-RO', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Bucharest'
    });

    private readonly dateFormatter = new Intl.DateTimeFormat('ro-RO', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        timeZone: 'Europe/Bucharest'
    });

    private readonly now$ = new Observable<Date>(observer => {
        const sub = this.ngZone.runOutsideAngular(() =>
            timer(0, 10000).pipe(map(() => this.serverTime.now())).subscribe({
                next: date => this.ngZone.run(() => observer.next(date)),
                error: e => this.ngZone.run(() => observer.error(e)),
                complete: () => this.ngZone.run(() => observer.complete()),
            })
        );
        return () => sub.unsubscribe();
    });

    async ngOnInit(): Promise<void> {
        await this.serverTime.calibrate();
        this.ngZone.runOutsideAngular(() => {
            const target = new Date();
            target.setHours(4, 0, 0, 0);
            if (target.getTime() <= Date.now()) target.setDate(target.getDate() + 1);
            this.reloadHandle = setTimeout(() => window.location.reload(), target.getTime() - Date.now());
        });
    }

    ngOnDestroy(): void {
        clearTimeout(this.reloadHandle);
    }

    readonly vm$ = combineLatest([
        this.readyBoardData.readyBoardState$,
        this.readyBoardData.source$,
        this.connectionService.indicator$,
        this.now$
    ]).pipe(
        map(([state, source, connectionIndicator, now]) => {
            const pickupStatusMap = new Map<string, string>();
            for (const p of (state.activePickups || [])) {
                if (p.childId) pickupStatusMap.set(p.childId, p.status);
                if (p.childDisplayName) {
                    pickupStatusMap.set(p.childDisplayName.trim().toLowerCase(), p.status);
                }
            }

            const presentChildren = (state.presentChildren || []).map(child => ({
                ...child,
                pickupStatus: pickupStatusMap.get(child.id)
                    ?? pickupStatusMap.get(child.name.trim().toLowerCase())
                    ?? null,
                cameraWatchers: child.cameraWatchers || []
            }));

            // Un rând per pereche (copil, părinte care urmărește) — pentru panoul "PĂRINȚI CONECTAȚI LIVE".
            const liveWatchNotices = presentChildren
                .filter(child => child.cameraWatchers.length > 0)
                .flatMap(child => child.cameraWatchers.map(watcher => ({
                    childName: child.name,
                    viewerName: watcher.viewerName,
                    cameraLabel: watcher.cameraLabel
                })));

            return {
                state,
                source,
                connectionIndicator,
                time: this.timeFormatter.format(now),
                date: this.dateFormatter.format(now),
                presentChildren,
                liveWatchNotices,
                internalAnnouncements: state.internalAnnouncements || [],
                schedule: state.schedule || []
            };
        })
    );
}
